import { ChevronRight } from 'lucide-react';
import type { CSSProperties, ReactNode, Ref } from 'react';
import { Link } from 'react-router';
import { cn } from '@/lib/utils';

/** Outlined panel that holds ruled rows. */
export const panelClasses = 'overflow-hidden rounded-md border border-border bg-surface';

/** Column header row of a ruled table. */
export const columnHeadClasses =
  'border-b border-border bg-sunken px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-subtle';

/** A link inside running text: underlined, in the link color. */
export const inlineLinkClasses =
  'focus-ring rounded-xs font-medium text-accent-soft-fg underline underline-offset-3';

/** Small uppercase label above a value. */
export const eyebrowClasses = 'text-xs font-semibold uppercase tracking-wider text-subtle';

/**
 * A link whose hit area covers its nearest `relative` ancestor (a table row); the focus outline is drawn around that
 * ancestor.
 */
export const stretchedLinkClasses =
  'outline-none after:absolute after:inset-0 hover:underline focus-visible:after:outline-2 focus-visible:after:-outline-offset-2 focus-visible:after:outline-ring';

/** Page frame: one width and gutter for every page. */
export function PageContainer({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) {
  return (
    <div
      className={cn('mx-auto w-full max-w-7xl px-4 pb-16 pt-6 sm:px-6 sm:pt-8 lg:px-8', className)}
    >
      {children}
    </div>
  );
}

export interface Crumb {
  label: string;
  to?: string;
}

export function Breadcrumbs({ items, className }: { items: Crumb[]; className?: string }) {
  return (
    <nav aria-label="Breadcrumb" className={cn('mb-3', className)}>
      <ol className="flex flex-wrap items-center gap-1 text-sm text-muted">
        {items.map((item, index) => (
          <li key={`${item.label}-${index}`} className="flex min-w-0 items-center gap-1">
            {index > 0 ? (
              <ChevronRight className="size-3.5 shrink-0 text-subtle" aria-hidden />
            ) : null}
            {item.to ? (
              <Link
                to={item.to}
                className="focus-ring min-w-0 rounded-xs [overflow-wrap:anywhere] hover:text-text hover:underline"
              >
                {item.label}
              </Link>
            ) : (
              <span aria-current="page" className="min-w-0 text-text [overflow-wrap:anywhere]">
                {item.label}
              </span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}

export interface Fact {
  label: string;
  value: ReactNode;
  /** Shown on hover, for example the full date. */
  title?: string;
  /** Sans text instead of mono. Defaults to mono. */
  mono?: boolean;
  /** Muted value, for "Unknown". */
  muted?: boolean;
  /**
   * Two columns wide from a 36rem strip, for ids and long version strings. Cells fill rows in order, so a wide fact
   * should complete a row.
   */
  wide?: boolean;
}

/** The most columns of a facts strip below a 36rem container, from 36rem, and from 64rem. */
const FACT_COLUMN_LIMITS = [2, 4, 8] as const;

/** The largest column count up to `limit` that `units` single-column cells fill completely, or 1. */
function fullColumns(units: number, limit: number): number {
  for (let columns = Math.min(units, limit); columns > 1; columns -= 1) {
    if (units % columns === 0) return columns;
  }
  return 1;
}

/** Column count of one width tier, and the span of wide facts in it. */
function factsTier(facts: readonly Fact[], limit: number, spanWide: boolean) {
  const wide = facts.filter((fact) => fact.wide).length;
  if (spanWide && wide > 0) {
    const columns = fullColumns(facts.length + wide, limit);
    if (columns >= 2) return { columns, span: 2 };
  }
  return { columns: fullColumns(facts.length, limit), span: 1 };
}

/**
 * Ruled cells of labelled values on a grid whose column count divides the cells, so every row is full and the rules
 * of all rows line up. Each cell draws its left and bottom rule and extends 1px past its grid area on those sides, so
 * the rules of the first column and the last row fall outside the clipped box and only inner rules show.
 */
export function FactsStrip({ facts, className }: { facts: readonly Fact[]; className?: string }) {
  const [narrow, medium, wide] = FACT_COLUMN_LIMITS.map((limit, tier) =>
    factsTier(facts, limit, tier > 0),
  );
  const grid = {
    '--facts-columns-1': narrow!.columns,
    '--facts-columns-2': medium!.columns,
    '--facts-columns-3': wide!.columns,
    '--facts-span-2': medium!.span,
    '--facts-span-3': wide!.span,
  } as CSSProperties;
  return (
    <div className={cn('@container/facts', className)}>
      <dl
        style={grid}
        className="grid grid-cols-[repeat(var(--facts-columns-1),minmax(0,1fr))] overflow-hidden rounded-md border border-border bg-surface @xl/facts:grid-cols-[repeat(var(--facts-columns-2),minmax(0,1fr))] @5xl/facts:grid-cols-[repeat(var(--facts-columns-3),minmax(0,1fr))]"
      >
        {facts.map((fact) => (
          <div
            key={fact.label}
            className={cn(
              '-mb-px -ml-px min-w-0 border-b border-l border-border px-3 py-1.5',
              fact.wide &&
                '@xl/facts:col-span-(--facts-span-2) @5xl/facts:col-span-(--facts-span-3)',
            )}
          >
            <dt className={eyebrowClasses}>{fact.label}</dt>
            <dd
              title={fact.title}
              className={cn(
                'text-xs [overflow-wrap:anywhere]',
                fact.mono === false ? 'font-sans' : 'font-mono',
                fact.muted ? 'text-subtle' : 'text-text',
              )}
            >
              {fact.value}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

/**
 * Head of a page: breadcrumbs, the level 1 title with optional mono meta and a one-line description, status and
 * actions at the end of the title row, and an optional facts strip. The title line is at least as tall as a small
 * button, so small actions bottom-aligned to the title column leave the h1 where it is on pages without actions.
 */
export function PageHeader({
  title,
  description,
  actions,
  children,
  breadcrumbs,
  meta,
  status,
  facts,
  headingRef,
  replaceTitle,
  className,
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  /** Rendered under the description, in the title column. */
  children?: ReactNode;
  breadcrumbs?: Crumb[];
  /** Mono text on the baseline of the title. */
  meta?: ReactNode;
  /** Rendered at the end of the title row, before the actions. */
  status?: ReactNode;
  facts?: readonly Fact[];
  /** The h1 takes `tabIndex={-1}` when set, so it can receive focus. */
  headingRef?: Ref<HTMLHeadingElement>;
  /** Shown instead of the visible title line; the h1 stays in the document, visually hidden. */
  replaceTitle?: ReactNode;
  className?: string;
}) {
  const tabIndex = headingRef ? -1 : undefined;
  return (
    <header className={cn('mb-4', className)}>
      {breadcrumbs ? <Breadcrumbs items={breadcrumbs} /> : null}
      <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-2">
        <div className="min-w-0">
          {replaceTitle ? (
            <>
              <h1 ref={headingRef} tabIndex={tabIndex} className="sr-only">
                {title}
              </h1>
              {replaceTitle}
            </>
          ) : (
            <div className="flex min-h-7 flex-wrap items-baseline gap-x-2.5 gap-y-0.5">
              <h1
                ref={headingRef}
                tabIndex={tabIndex}
                className="min-w-0 break-words text-xl font-semibold text-text outline-none"
              >
                {title}
              </h1>
              {meta ? (
                <p className="min-w-0 font-mono text-xs text-subtle [overflow-wrap:anywhere]">
                  {meta}
                </p>
              ) : null}
            </div>
          )}
          {description ? (
            <div className="mt-1 max-w-prose text-sm text-muted">{description}</div>
          ) : null}
          {children}
        </div>
        {status || actions ? (
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            {status}
            {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
          </div>
        ) : null}
      </div>
      {facts && facts.length > 0 ? <FactsStrip facts={facts} className="mt-3" /> : null}
    </header>
  );
}

/**
 * A titled section of a page. It has no accessible name, so it is not a region landmark. Actions sit at the end of
 * the heading line, and the heading and description wrap beside them, except in the left column of an `aside`
 * section, where they follow the description.
 */
export function PageSection({
  title,
  description,
  actions,
  aside = false,
  className,
  children,
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  /** From a 42rem container width, the heading and description sit in a left column. */
  aside?: boolean;
  className?: string;
  children: ReactNode;
}) {
  return (
    <section className={cn('@container/section', className)}>
      <div
        className={cn(
          aside &&
            '@2xl/section:grid @2xl/section:grid-cols-[13rem_minmax(0,1fr)] @2xl/section:gap-x-8',
        )}
      >
        <div
          className={cn(
            'mb-3 flex items-start justify-between gap-x-4',
            aside && '@2xl/section:mb-0 @2xl/section:block',
          )}
        >
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-text">{title}</h2>
            {description ? (
              <p className="mt-0.5 max-w-prose text-sm text-muted">{description}</p>
            ) : null}
          </div>
          {actions ? (
            <div
              className={cn(
                'flex shrink-0 flex-wrap items-center gap-2',
                aside && '@2xl/section:mt-3',
              )}
            >
              {actions}
            </div>
          ) : null}
        </div>
        <div className="min-w-0">{children}</div>
      </div>
    </section>
  );
}
